/**
 * `globalShortcut.register` throws on anything it cannot parse, so combos are screened
 * before they reach it or a persisted store. Modifier-only combos such as Hummingbird's
 * `option+option` are rejected too: they belong to ContextKit's tap monitor.
 */

const MODIFIER_TOKENS = new Set(['command', 'control', 'option', 'shift']);

/**
 * `fn` is deliberately absent: macOS puts its own dictation on a bare Globe press, so
 * tapping it is not ours to take. Push to talk holds it instead.
 */
const TAP_MODIFIERS = ['option', 'command', 'control', 'shift'] as const;

/**
 * Ordered by how safe they are to hold. Only `fn` is unused on its own; the rest are held
 * for seconds during Option+click, Command+drag and the like, so they fire constantly.
 */
export const HOLD_MODIFIERS = [
  'fn',
  'control',
  'shift',
  'option',
  'command',
] as const;

export type HoldModifier = (typeof HOLD_MODIFIERS)[number];

/** Empty means push to talk is off, so an unset binding is a valid one. */
export function parseHoldShortcut(combo: string): HoldModifier | null {
  return HOLD_MODIFIERS.find((modifier) => modifier === combo) ?? null;
}

const MAX_TAPS = 5;

const NAMED_KEY_TOKENS = new Set([
  'space',
  'enter',
  'escape',
  'tab',
  'backspace',
  'delete',
  'up',
  'down',
  'left',
  'right',
]);

const isPrintableAsciiKey = (token: string): boolean =>
  token.length === 1 && token > ' ' && token <= '~' && token !== '+';

const isKeyToken = (token: string): boolean =>
  isPrintableAsciiKey(token) ||
  /^f([1-9]|1\d|2[0-4])$/.test(token) ||
  NAMED_KEY_TOKENS.has(token);

export function isRegisterableAccelerator(combo: string): boolean {
  const tokens = combo.split('+');
  const key = tokens.pop();
  const modifiers = tokens;

  return (
    key !== undefined &&
    isKeyToken(key) &&
    modifiers.length > 0 &&
    modifiers.every((modifier) => MODIFIER_TOKENS.has(modifier)) &&
    new Set(modifiers).size === modifiers.length
  );
}

export type TapModifier = (typeof TAP_MODIFIERS)[number];

export type ModifierTapBinding = {
  /** Sorted, so two spellings of the same chord compare equal */
  modifiers: TapModifier[];
  taps: number;
};

const asTapModifier = (token: string): TapModifier | undefined =>
  TAP_MODIFIERS.find((key) => key === token);

/**
 * A token repeated is a tap count and tokens that differ are a chord, so `option+option`
 * is two taps of Option and `command+option` is one press of the two together.
 */
export function parseModifierTapShortcut(
  combo: string
): ModifierTapBinding | null {
  const tokens = combo.split('+');
  if (tokens.length < 2) return null;

  const modifiers = tokens.map(asTapModifier);
  if (modifiers.some((modifier) => modifier === undefined)) return null;

  const distinct = [...new Set(modifiers as TapModifier[])];
  if (distinct.length === 1) {
    return tokens.length > MAX_TAPS
      ? null
      : { modifiers: distinct, taps: tokens.length };
  }

  return distinct.length === tokens.length
    ? { modifiers: distinct.sort(), taps: 1 }
    : null;
}

export function isBindableShortcut(combo: string): boolean {
  return (
    isRegisterableAccelerator(combo) || parseModifierTapShortcut(combo) !== null
  );
}

/** Nothing arbitrates between two tap bindings on one key, so both would fire. */
export function tapBindingsCollide(combo: string, other: string): boolean {
  const binding = parseModifierTapShortcut(combo);
  const otherBinding = parseModifierTapShortcut(other);
  if (!binding || !otherBinding) return false;

  // Different sets are different flag states, so only the same one has no arbiter
  return (
    binding.modifiers.length === otherBinding.modifiers.length &&
    binding.modifiers.every(
      (key, index) => key === otherBinding.modifiers[index]
    )
  );
}

/** The tap-then-hold the voice shortcut rides on when Hover's own shortcut is not a tap binding. */
export const HUMMINGBIRD_VOICE_HOLD_SHORTCUT = 'option+option';

/**
 * The single key Hover's native binding holds: its tap shortcut's, or the voice hold's for a
 * regular accelerator. A chord holds none.
 */
export function hummingbirdHeldModifier(
  hummingbirdShortcut: string
): HoldModifier | null {
  const binding =
    parseModifierTapShortcut(hummingbirdShortcut) ??
    parseModifierTapShortcut(HUMMINGBIRD_VOICE_HOLD_SHORTCUT);
  if (!binding || binding.modifiers.length !== 1) return null;
  return parseHoldShortcut(binding.modifiers[0]);
}
